import { css } from '@emotion/react';
import type { BoxProps } from '@mui/joy';
import { Box, styled } from '@mui/joy';
import { useMap } from '@vis.gl/react-maplibre';
import type { PaddingOptions } from 'maplibre-gl';
import { useEffect, useImperativeHandle, useRef } from 'react';

import { useScreenSize } from '@/hooks/useScreenSize';
import { MAIN_BREAKPOINT } from '@/modules/MAIN_BREAKPOINT';
import { useMapStore } from '@/stores/useMapStore';

const Root = styled(Box)(
  ({ theme }) => css`
    position: absolute;
    max-height: 100%;
    z-index: 3;

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      width: 35%;
      max-width: ${theme.spacing(64)};
    }
  `,
);

type MapContentProviderProps = {
  placement: { mobile: keyof PaddingOptions; desktop: keyof PaddingOptions };
  // ref?: Ref<HTMLDivElement>;
} & BoxProps;

export function MapContentProvider({ placement, ref: forwardedRef, ...boxProps }: MapContentProviderProps) {
  const ref = useRef<HTMLDivElement>(null);
  useImperativeHandle(forwardedRef, () => ref.current as HTMLDivElement);

  const { setPadding } = useMapStore((state) => state.actions);
  const map = useMap();

  const { screenSize } = useScreenSize();
  const screenPlacement = placement[screenSize];

  useEffect(() => {
    let resizeObserver: ResizeObserver;
    if (ref.current) {
      resizeObserver = new ResizeObserver(() => {
        const rect = ref.current?.getBoundingClientRect();

        const padding = {
          top: rect?.height,
          right: rect?.width,
          bottom: rect?.height,
          left: rect?.width,
        };

        if (padding[screenPlacement]) {
          setPadding({
            [screenPlacement]: padding[screenPlacement],
          });
        }
      });

      resizeObserver.observe(ref.current);
    }

    return () => {
      setPadding({ [screenPlacement]: 0 });
      resizeObserver.disconnect();
    };
  }, [screenPlacement, setPadding]);

  useEffect(() => {
    const mapCurrent = map.current;

    return () => {
      mapCurrent?.flyTo({ padding: { bottom: 0 } });
    };
  }, [map, setPadding]);

  return <Root {...boxProps} ref={ref} />;
}
